import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useActivePrizeDraw, useBuyPrizeTicket } from '@/features/games/prizeDraw';
import { useAuth } from '@/lib/auth/AuthProvider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

function countdown(iso: string, ar: boolean): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return ar ? 'السحب قريّب' : 'drawing soon';
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return ar ? `${d} يوم ${h} ساعة` : `${d}d ${h}h`;
  const m = Math.floor((ms % 3600000) / 60000);
  return ar ? `${h} ساعة ${m} دقيقة` : `${h}h ${m}m`;
}

function Stat({ icon, label, value }: { icon: keyof typeof Ionicons.glyphMap; label: string; value: string }) {
  const palette = useThemePalette();
  return (
    <View className="flex-1 items-center gap-2 rounded-2xl border border-glass-border bg-glass px-2 py-3.5">
      <View className="h-8 w-8 items-center justify-center rounded-full border border-volt-line bg-volt-glass">
        <Ionicons name={icon} size={15} color={palette.voltBright} />
      </View>
      <AppText variant="heading" weight="bold" tabular className="text-ink">{value}</AppText>
      <AppText variant="micro" className="text-center text-muted" numberOfLines={1}>{label}</AppText>
    </View>
  );
}

export default function PrizeDrawScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions, isRTL } = useLocale();
  const { userId } = useAuth();
  const toast = useToast();
  const q = useActivePrizeDraw();
  const buy = useBuyPrizeTicket();
  const howto = useHowToPlay('prizeDraw');

  const num = (n: number) => formatNumber(n, numeralOptions);
  const draw = q.data?.draw ?? null;
  const winner = q.data?.last_winner ?? null;

  // Celebrate once when the caller is the most recent winner.
  const iWon = !!winner && !!userId && winner.winner_user_id === userId;
  const [celebrate, setCelebrate] = useState(false);
  const checkedWin = useRef(false);
  useEffect(() => {
    if (checkedWin.current || !iWon || !winner) return;
    checkedWin.current = true;
    AsyncStorage.getItem(`tashkeela.prizeWon.${winner.drawn_at}`)
      .then((seen) => {
        if (!seen) setCelebrate(true);
      })
      .catch(() => {});
  }, [iWon, winner]);
  const dismissWin = () => {
    setCelebrate(false);
    if (winner) AsyncStorage.setItem(`tashkeela.prizeWon.${winner.drawn_at}`, '1').catch(() => {});
  };

  const onBuy = () => {
    if (!draw) return;
    buy.mutate(draw.id, {
      onSuccess: () => toast.show({ message: t('prizeDraw.bought'), tone: 'success' }),
      onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
    });
  };

  const canAfford = draw ? draw.my_coins >= draw.ticket_cost : false;

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="prizeDraw" icon="gift" />
      <BackHeader title={t('prizeDraw.title')} right={<HowToPlayButton onPress={howto.open} />} />
      {q.isPending ? (
        <View className="gap-3 px-4 pt-2">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-20 w-full" />
        </View>
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !draw ? (
        <View className="flex-1 items-center justify-center px-8">
          <Ionicons name="gift-outline" size={40} color={palette.muted} />
          <AppText variant="caption" className="mt-3 text-center text-soft">{t('prizeDraw.none')}</AppText>
        </View>
      ) : (
        <ScrollView contentContainerStyle={{ paddingBottom: 40, gap: 14 }}>
          {celebrate && winner ? (
            <View className="mx-4 mt-1 overflow-hidden rounded-card border border-volt">
              <LinearGradient
                colors={[palette.captain + '33', palette.volt + '22']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{ padding: 16 }}
              >
                <View className="flex-row items-center gap-3">
                  <Ionicons name="trophy" size={26} color={palette.captain} />
                  <View className="flex-1">
                    <AppText variant="caption" weight="bold" className="text-ink">{t('prizeDraw.youWonTitle')}</AppText>
                    <AppText variant="micro" className="text-soft">
                      {t('prizeDraw.youWonBody', { pot: num(winner.pot_coins) })}
                    </AppText>
                  </View>
                  <Pressable onPress={dismissWin} hitSlop={8}>
                    <Ionicons name="close" size={18} color={palette.muted} />
                  </Pressable>
                </View>
              </LinearGradient>
            </View>
          ) : null}

          <AppText variant="caption" className="mx-4 mt-1 text-center text-soft">{t('prizeDraw.intro')}</AppText>

          {/* The pot */}
          <View className="mx-4 overflow-hidden rounded-card border border-volt/30">
            <LinearGradient colors={[palette.volt + '26', 'rgba(255,255,255,0)']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={{ padding: 22, alignItems: 'center' }}>
              <View className="h-14 w-14 items-center justify-center rounded-full border border-captain/40" style={{ backgroundColor: palette.captain + '1F' }}>
                <Ionicons name="trophy" size={28} color={palette.captain} />
              </View>
              <AppText variant="micro" className="mt-3 uppercase tracking-widest text-soft">{t('prizeDraw.pot')}</AppText>
              <View className="mt-1.5 flex-row items-end gap-2">
                <AppText weight="bold" tabular className="text-ink" style={{ fontSize: 46, lineHeight: 48 }}>{num(draw.pot_coins)}</AppText>
                <AppText variant="caption" weight="bold" className="mb-2.5 text-volt-bright">{t('store.balance')}</AppText>
              </View>
              <View className="mt-3 flex-row items-center gap-1.5 rounded-full border border-glass-border bg-glass px-3 py-1.5">
                <Ionicons name="time-outline" size={13} color={palette.voltBright} />
                <AppText variant="micro" weight="medium" className="text-soft">
                  {t('prizeDraw.drawsIn', { time: countdown(draw.draws_at, isRTL) })}
                </AppText>
              </View>
            </LinearGradient>
          </View>

          {/* Stats */}
          <View className="mx-4 flex-row gap-2.5">
            <Stat icon="ticket-outline" label={t('prizeDraw.myTickets')} value={num(draw.my_tickets)} />
            <Stat icon="people-outline" label={t('prizeDraw.entrants')} value={num(draw.total_entrants)} />
            <Stat icon="layers-outline" label={t('prizeDraw.totalTickets')} value={num(draw.total_tickets)} />
          </View>

          {/* Buy */}
          <Card className="mx-4 gap-4 px-4 py-4">
            <View className="flex-row items-center justify-between">
              <AppText variant="caption" weight="semibold" className="text-soft">{t('prizeDraw.balance')}</AppText>
              <View className="flex-row items-center gap-1.5 rounded-full border border-captain/30 px-2.5 py-1" style={{ backgroundColor: palette.captain + '14' }}>
                <Ionicons name="logo-bitcoin" size={14} color={palette.captain} />
                <AppText variant="caption" weight="bold" tabular className="text-ink">{num(draw.my_coins)}</AppText>
              </View>
            </View>
            {userId ? (
              <View className="gap-2.5 border-t border-glass-border pt-4">
                <Button
                  label={t('prizeDraw.buy', { cost: num(draw.ticket_cost) })}
                  loading={buy.isPending}
                  disabled={!canAfford}
                  onPress={onBuy}
                  className="bg-volt-glass-strong"
                />
                {!canAfford ? (
                  <AppText variant="micro" className="text-center text-muted">{t('prizeDraw.needCoins')}</AppText>
                ) : (
                  <AppText variant="micro" className="text-center text-muted">{t('prizeDraw.oddsNote')}</AppText>
                )}
              </View>
            ) : (
              <AppText variant="micro" className="text-center text-muted">{t('prizeDraw.signIn')}</AppText>
            )}
          </Card>

          {/* Last winner */}
          {winner ? (
            <View className="mx-4 flex-row items-center gap-3 rounded-card border border-line bg-surface px-4 py-3">
              <Ionicons name="ribbon" size={20} color={palette.captain} />
              <View className="flex-1">
                <AppText variant="micro" className="uppercase tracking-wide text-muted">{t('prizeDraw.lastWinner')}</AppText>
                <AppText variant="caption" weight="bold" className="text-ink" numberOfLines={1}>
                  {t('prizeDraw.wonPot', { name: winner.winner_name, pot: num(winner.pot_coins) })}
                </AppText>
              </View>
            </View>
          ) : null}
        </ScrollView>
      )}
    </Screen>
  );
}
